import { apolloClient, ConnectedPageInfo, createSliceFoundation, getBaseImpures, getBaseReducers, PropsFrom, Utils, BigState } from "@crispico/foundation-react";
import { AuthenticationStatus, LoginParamsInput } from "@crispico/foundation-react/apollo-gen-foundation/globalTypes";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import React from "react";
import { Link, Redirect } from "react-router-dom";
import { Button, CheckboxProps, Form, Grid, Icon, InputOnChangeData, Label, Message, Segment, Tab, TabProps, SemanticShorthandItem, TabPaneProps } from "semantic-ui-react";
import { login, loginVariables } from "../../apollo-gen-foundation/login";
import { HomePage, HomePageProps, infoHomePage } from "../HomePage";
import { expiredPasswordResetPageUrl, passwordResetBaseUrl } from "../passwordReset/PasswordResetFinishPage";
import { passwordResetInitPageUrl, passwordResetInitPageUrl_twoManyFailedLogins } from "../passwordReset/PasswordResetInitPage";
import { LOGIN } from "./queries";
import { userEntityDescriptor } from "../user/userEntityDescriptor";

const cookieMaxAge = `;max-age=2592000`;

export const LoginTypeEnum = {
    STANDARD: "STANDARD",
    LDAP: "LDAP"
  } as const;
export type LoginTypeEnum = typeof LoginTypeEnum[keyof typeof LoginTypeEnum];


export const sliceLoginPage = createSliceFoundation(class SliceLoginPage {

    reducers = {
        ...getBaseReducers<SliceLoginPage>(this),
    }
    impures = {
        ...getBaseImpures<SliceLoginPage>(this),

        async login(username: string, password: string, rememberMe: boolean, loginType: string, callback: (status: AuthenticationStatus, message?: string) => void) {
            var loginParams: LoginParamsInput = { username: username, password: password, rememberMe: rememberMe, loginType: loginType }

            const response = (await apolloClient.mutate<login, loginVariables>({
                mutation: LOGIN,
                variables: { loginParams: loginParams }
            })).data?.authService_login;


            if (response!.token) {
                // when processing a GraphQL request server-side, we don't have a access to a HTTP response
                // to append a cookie, so it is set here, after a succesful login with remember-me selected.
                document.cookie = `auth_token=` + response!.token + cookieMaxAge;
            }

            if (response!.status === AuthenticationStatus.SUCCESS || response!.status === AuthenticationStatus.SUCCESS_WITH_EXPIRED_PASSWORD) {
                await AppMetaTempGlobals.appMetaInstance.afterLogin();
            }
            callback(response!.status!, _msg(response!.messageKey || "general.error", response!.messageParams));
        }
    }
})

type LocalState = {
    username: string,
    password: string,
    rememberMe: boolean,
    showPassword: boolean,
    loggedInStatus: string | null,
    error: string | undefined,
    loginType: string
};
type LoginPageProps = PropsFrom<typeof sliceLoginPage> & HomePageProps & {
    ldapAvailable: boolean;
};

export class LoginPage extends HomePage<LoginPageProps, LocalState> {

    constructor(props: LoginPageProps) {
        super(props);
        this.onSubmit = this.onSubmit.bind(this);
        this.onTabChanged = this.onTabChanged.bind(this);
        this.loginCallback = this.loginCallback.bind(this);

        const loginTypeFromSession = window.sessionStorage.getItem("login.type");
        this.state = {
            username: "", password: "", rememberMe: false, showPassword: false, loggedInStatus: null, error: undefined,
            loginType: loginTypeFromSession ? loginTypeFromSession! : LoginTypeEnum.STANDARD
        };
    }

    protected onTabChanged(event: React.MouseEvent<HTMLDivElement>, data: TabProps) {
        const activePane = data.panes![data.activeIndex as number].pane! as TabPaneProps;
        let loginType = activePane ? activePane.loginType : LoginTypeEnum.STANDARD;
        // keep login type in session storage
        window.sessionStorage.setItem("login.type", loginType);
        this.setState({ username: "", password: "", showPassword: false, loggedInStatus: null, error: undefined, loginType: loginType });
    }

    protected verifyCredentials(username: string, password: string) {
        if (username === "" || password === "") {
            this.loginCallback(AuthenticationStatus.FAILURE, _msg("login.notnull"));
            return false;
        }
        return true;
    }

    protected loginCallback(status: AuthenticationStatus, message: any) {
        if (status === AuthenticationStatus.FAILURE) {
            this.setState({ error: message });
        } else {
            this.setState({ loggedInStatus: status, username: "", password: "" });
        }
    }
    protected onSubmit() {
        if (!this.verifyCredentials(this.state.username, this.state.password)) {
            return;
        }
        this.props.dispatchers.login(this.state.username, this.state.password, this.state.rememberMe, this.state.loginType, this.loginCallback);
    }

    protected showPassword(prevShowPassword: boolean) {
        this.setState({ showPassword: !prevShowPassword })
    }

    // extra function to be easy to extend and overwrite
    protected getPanes(): {
        pane?: SemanticShorthandItem<TabPaneProps>
        menuItem?: any
        render?: () => React.ReactNode
    }[] {
        const panes = [
            {
                pane: { loginType: LoginTypeEnum.STANDARD as string },
                menuItem: { key: LoginTypeEnum.STANDARD as string, icon: userEntityDescriptor.getIcon(), content: _msg("login.standard") },
                render: () => this.renderStandardLoginTab()
            }
        ]
        if (this.props.ldapAvailable) {
            panes.push({
                pane: { loginType: LoginTypeEnum.LDAP as string },
                menuItem: { key: LoginTypeEnum.LDAP as string, icon: userEntityDescriptor.getIcon(), content: _msg("login.ldap") },
                render: () => this.renderLdapLoginTab()
            });
        }

        return panes;
    }

    protected renderUsernameInput(placeholder: string, autoFocus?: boolean) {
        return <Form.Input fluid icon='user' iconPosition='left' placeholder={placeholder} value={this.state.username}
            autoFocus={autoFocus}
            onChange={(evt: any, data: InputOnChangeData) => this.setState({ username: data.value })} />;
    }

    protected renderPasswordInput(placeholder: string, autoFocus?: boolean) {
        return <Form.Input fluid iconPosition='left' labelPosition='right corner' placeholder={placeholder} type={this.state.showPassword ? 'text' : 'password'} value={this.state.password}
            onChange={(evt: any, data: InputOnChangeData) => this.setState({ password: data.value })}>
            <Icon name='lock' />
            <input autoFocus={autoFocus} autoComplete='off' />
            <Label attached="top right" style={{ background: "transparent" }}><Icon name={this.state.showPassword ? 'eye slash' : 'eye'} className="no-margin" link onClick={() => this.showPassword(this.state.showPassword)} /></Label>
        </Form.Input>
    }

    protected renderStandardLoginTab() {
        return <>
            <Form error onSubmit={this.onSubmit} className="attached segment">
                {this.renderUsernameInput(_msg("login.username"), true)}
                {this.renderPasswordInput(_msg("login.password"))}
                {global.rememberMeEnabled ? <Form.Checkbox label={'Remember me'}
                    onChange={(evt: any, data: CheckboxProps) => this.setState({ rememberMe: data.checked ? data.checked : false })} /> : null}
                <Button primary fluid size='large'>{_msg("login.signIn")}</Button>
                {this.state.error ? <Message style={{ textAlign: 'left' }} error header={_msg("alert.loginFailed")} content={this.state.error} /> : null}
            </Form>
            <Message attached="bottom" warning style={{ fontWeight: 'bold' }}>
                <Link to={passwordResetInitPageUrl}>
                    <span>Did you forget your password?</span>
                </Link>
            </Message>
            {global.oktaAuthenticationAvailable ? <Segment>
                <h4>Sign in with: </h4>
                {/* In development add "http://localhost:8080/" to the link to use Okta */}
                <a className="ThirdPartyLoginButton" href={Utils.adjustUrlToServerContext("oauth2/authorization/okta")}><img src="https://www.okta.com/themes/custom/okta_www_theme/images/logo.svg?v2" width="60px" alt="Sign in with Okta" /></a>
            </Segment> : null}
        </>
    }

    protected renderLdapLoginTab() {
        return <Form error onSubmit={this.onSubmit} className="attached segment">
            {this.renderUsernameInput(_msg("login.ldap") + " " + _msg("login.username"), true)}
            {this.renderPasswordInput(_msg("login.password"))}
            <Button primary fluid size='large'>{_msg("login.signIn")}</Button>
            {this.state.error ? <Message style={{ textAlign: 'left' }} error header={_msg("alert.loginFailed")} content={this.state.error} /> : null}
        </Form>
    }

    protected renderUnderJumbotron() {
        const panes = this.getPanes();
        var tabs = null;

        if (panes.length > 1) {
            tabs = <Tab menu={{ tabular: false, attached: true }} onTabChange={this.onTabChanged} activeIndex={panes.findIndex(p => (p.pane as TabPaneProps)?.loginType === this.state.loginType)} panes={panes} />;
        } else {
            tabs = panes[0].render?.();
        }

        return (<Grid textAlign='center' verticalAlign='middle'>
            <Grid.Row>
                <Grid.Column style={{ maxWidth: 450 }}>
                    {tabs}
                </Grid.Column>
            </Grid.Row>          
            {this.renderAdditionalRows()}
        </Grid>)
    }

    protected renderAdditionalRows() {
    }

    private getRedirectFromPrevious() {
        const from = this.props.location?.state?.from;
        if (!from || !from.pathname) {
            return undefined;
        }
        return from.pathname + (from.search ? from.search : "");
    }

    private isRedirectAllowedWithoutUser() {
        const redirectFromPrevious = this.getRedirectFromPrevious();
        if (redirectFromPrevious?.startsWith(passwordResetBaseUrl)) {
            return true;
        }
        return false;
    }

    render() {
        if (AppMetaTempGlobals.appMetaInstance.getCurrentUser() !== null || this.isRedirectAllowedWithoutUser()) {
            return <Redirect to={this.getRedirectFromPrevious() || { pathname: '/' }} />;
        }
        if (this.state.loggedInStatus) {
            var redirectTo;
            if (this.state.loggedInStatus === AuthenticationStatus.SUCCESS) {
                redirectTo = this.getRedirectFromPrevious() || { pathname: '/' };
            } else if (this.state.loggedInStatus === AuthenticationStatus.SUCCESS_WITH_EXPIRED_PASSWORD) {
                redirectTo = expiredPasswordResetPageUrl;
            } else { //AuthenticationStatus.FAILURE_TWO_MANY_FAILED_LOGINS
                redirectTo = passwordResetInitPageUrl_twoManyFailedLogins;
            }
            return <Redirect to={redirectTo} />
        }
        return super.renderMain();
    }

}

export const infoLoginPage = new ConnectedPageInfo(sliceLoginPage, LoginPage, "LoginPage", undefined, false);
infoLoginPage.routeProps = { path: "/login", exact: true };
const infoLoginPageMapBigStateToPropsSuper = infoHomePage.mapBigStateToProps;
infoLoginPage.mapBigStateToProps = (state: BigState, props: LoginPageProps) => {
    infoLoginPageMapBigStateToPropsSuper!(state, props);
    props.ldapAvailable = state.AppContainer.initializationsForClient.ldapAvailable;
}
